/**
 * LiquidProgressBar — bouncy spring progress bar with overshoot
 */

import { motion, useSpring, useTransform } from 'framer-motion';
import { useEffect } from 'react';
import { useReducedMotion } from '../../hooks/useReducedMotion';

interface LiquidProgressBarProps {
  percentage: number;
  color?: string;
  className?: string;
  height?: string;
  glow?: boolean;
}

export default function LiquidProgressBar({
  percentage,
  color,
  className = '',
  height = 'h-4',
  glow = false,
}: LiquidProgressBarProps) {
  const reduced = useReducedMotion();
  const clamped = Math.max(0, Math.min(100, percentage));

  const springValue = useSpring(0, { stiffness: 100, damping: 15 });
  const width = useTransform(springValue, (v) => `${v}%`);

  useEffect(() => {
    springValue.set(clamped);
  }, [clamped, springValue]);

  if (reduced) {
    return (
      <div className={`${height} bg-kid-bg rounded-full overflow-hidden ${className}`}>
        <div
          className={`h-full rounded-full transition-[width] duration-300`}
          style={{ width: `${clamped}%`, backgroundColor: color }}
        />
      </div>
    );
  }

  return (
    <div className={`relative ${height} bg-kid-bg rounded-full overflow-hidden ${className}`}>
      <motion.div
        className="h-full rounded-full relative"
        style={{
          width,
          backgroundColor: color,
          boxShadow: glow ? `0 0 12px ${color}40` : undefined,
        }}
      >
        {/* Shine highlight */}
        <div
          className="absolute inset-0 rounded-full"
          style={{
            background: 'linear-gradient(180deg, rgba(255,255,255,0.35) 0%, transparent 60%)',
          }}
        />
      </motion.div>
    </div>
  );
}
